import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Lightbulb, Globe, Users, BookOpen, Brain, Cpu, Atom, Zap, Scale, Shield, Binary } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export const AboutPage = () => {
  return (
    <div id="main-content" className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="overline text-brand-primary mb-4">About the Centre</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900 leading-tight mb-6">
              Centre for Consciousness Studies Australia
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
              The Centre for Consciousness Studies Australia is dedicated to advancing rigorous interdisciplinary research across consciousness studies, neurotechnology, artificial intelligence, fundamental physics, and the deeper scientific and philosophical foundations of mind, reality, and STEM disciplines.
            </p>
          </div>
        </div>
      </section>

      {/* Institutional Overview */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
                Why the Centre Was Established
              </h2>
              <div className="prose-custom space-y-4">
                <p>
                  The Centre for Consciousness Studies Australia was founded in recognition of a critical need: as transformative technologies in artificial intelligence and neurotechnology reshape our world, humanity requires deeper understanding of the very nature of consciousness, mind, and intelligence.
                </p>
                <p>
                  Traditional disciplinary boundaries have often fragmented our understanding of these foundational questions. The Centre brings together researchers across philosophy, neuroscience, physics, computer science, and ethics to pursue integrated, rigorous inquiry that can inform both scientific advancement and wise governance.
                </p>
                <p>
                  Situated in Australia but globally connected, the Centre contributes to international scholarship while addressing the distinctive policy and research needs of the Australian context.
                </p>
              </div>
            </div>
            <div>
              <div className="rounded-lg overflow-hidden shadow-lg bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 relative aspect-[4/3] min-h-[320px]">
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 50% at 30% 40%, rgba(59,130,246,0.25), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 70%, rgba(139,92,246,0.18), transparent 60%)",
                  }}
                />
                <div
                  className="absolute inset-0 opacity-[0.08]"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                  }}
                />
                <div className="relative h-full w-full flex items-center justify-center p-10 text-center">
                  <div>
                    <p className="overline text-blue-300 mb-3">Interdisciplinary Inquiry</p>
                    <p className="font-serif text-2xl text-white leading-snug">
                      Consciousness · Mind · Intelligence · Reality
                    </p>
                    <p className="text-sm text-slate-400 mt-3 max-w-sm mx-auto">
                      Bridging philosophy, neuroscience, physics, and computer science at a moment of rapid technological change.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 lg:py-24 bg-white" data-testid="vision-mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-brand-highlight">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-lg bg-brand-highlight flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-brand-primary" />
                </div>
                <h3 className="font-serif text-2xl text-slate-900 mb-4">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed">
                  We envision a world in which consciousness is recognised as a fundamental aspect of reality and becomes central to how humanity understands mind, intelligence, technology, and the universe. Grounded in this vision, our research advances emerging scientific and technological disciplines in ways that strengthen human agency, ethical responsibility, psychological wellbeing, and sustainable human flourishing. By bridging consciousness studies, fundamental physics, health and wellbeing, artificial intelligence, neurotechnology, and governance, we seek to shape a future in which transformative technologies serve humanity without reducing life to a purely mechanistic view of the world.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-brand-highlight">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-lg bg-brand-highlight flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-brand-primary" />
                </div>
                <h3 className="font-serif text-2xl text-slate-900 mb-4">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  Our mission is to advance interdisciplinary research that investigates consciousness as a fundamental aspect of reality and applies this knowledge to the development of science, emerging technologies, ethics, and human wellbeing. We are committed to producing ideas, evidence, and frameworks that protect human agency, strengthen moral and psychological flourishing, support safe and responsible innovation, and contribute to a more sustainable future.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Contributions */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="contributions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="overline text-brand-primary mb-4">Our Impact</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-4">
              Core Contributions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The Centre contributes to scholarship and society through multiple channels of research, engagement, and education.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="font-serif text-xl text-slate-900 mb-3">Research Excellence</h3>
              <p className="text-slate-600 text-sm">
                Producing high-quality research across consciousness studies, philosophy of mind, AI ethics, neurotechnology, and fundamental physics.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="font-serif text-xl text-slate-900 mb-3">Policy Engagement</h3>
              <p className="text-slate-600 text-sm">
                Informing policy development on neurotechnology, AI governance, and emerging technologies through evidence-based analysis and submissions.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="font-serif text-xl text-slate-900 mb-3">Public Understanding</h3>
              <p className="text-slate-600 text-sm">
                Making complex research accessible through videos, essays, and public engagement activities that foster informed discourse.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="font-serif text-xl text-slate-900 mb-3">Thought Leadership</h3>
              <p className="text-slate-600 text-sm">
                Contributing to global conversations on consciousness, AI, and neurotechnology through conferences, publications, and collaborations.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="font-serif text-xl text-slate-900 mb-3">Interdisciplinary Integration</h3>
              <p className="text-slate-600 text-sm">
                Bridging disciplinary boundaries to develop integrated frameworks for understanding mind, intelligence, and reality.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="font-serif text-xl text-slate-900 mb-3">Future-Oriented Research</h3>
              <p className="text-slate-600 text-sm">
                Anticipating emerging questions and challenges at the intersection of consciousness, AI, and neurotechnology.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainable AI Connection */}
      <section className="py-16 lg:py-24 bg-white" data-testid="sustainable-ai-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="overline text-brand-primary mb-4">Strategic Context</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Connection to Sustainable AI
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              The Centre for Consciousness Studies Australia operates within a broader strategic context that includes the Centre for Sustainable AI. While our primary focus is on fundamental research into consciousness and mind, we recognize that responsible AI development requires deep engagement with questions of consciousness, cognition, and ethics.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              Our work on AI consciousness, machine cognition, and the ethical governance of AI systems directly contributes to the broader goal of ensuring AI development serves humanity sustainably and responsibly.
            </p>
            <Button
              asChild
              variant="outline"
              className="border-slate-300"
              data-testid="learn-more-research-btn"
            >
              <Link to="/research">
                Explore Our Research <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* The Convergence */}
      <section className="py-16 lg:py-24 bg-white" data-testid="convergence-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="overline text-brand-primary mb-4">The Convergence</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Five Domains Reshaping Our World
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We are entering a period in which consciousness studies, artificial intelligence, neurotechnology, fundamental physics, and quantum computing can no longer be treated as separate conversations. In this research agenda, they are approached as deeply interconnected domains that together reshape how we understand mind, reality, agency, and responsible technological design.
            </p>
          </div>
          {/* Featured: Consciousness Studies */}
          <Card className="border-2 border-brand-highlight bg-gradient-to-br from-blue-50 to-white mb-8">
            <CardContent className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-[auto,1fr] gap-8 items-start">
                <div className="w-20 h-20 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-10 h-10 text-blue-600" />
                </div>
                <div>
                  <p className="overline text-brand-primary mb-3">Central Domain</p>
                  <h3 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-4">Consciousness Studies</h3>
                  <p className="text-slate-600 leading-relaxed text-lg mb-6">
                    Consciousness studies sits at the centre of this research program. The work examines consciousness not simply as a by-product of the brain, but as the basis of awareness, causation, time perception, and subjective experience. Across these publications, consciousness is explored as a discrete, sequential process and as a possible missing dimension in current attempts to relate mind, physics, and intelligence.
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-600">
                    <li>• Sequential awareness, causation, and time perception</li>
                    <li>• Subjectivity–objectivity coupling and fundamental experience</li>
                    <li>• Limits of language, logic, self-reference, and computation</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Four equal domains */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                  <Cpu className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-serif text-xl text-slate-900 mb-3">Artificial Intelligence</h3>
                <p className="text-slate-600 leading-relaxed text-sm mb-4">
                  Artificial intelligence in this research is not only about building more capable systems. It is also about testing the limits of computation, explainability, and machine understanding. The work argues that AI can replicate important aspects of cognition, yet subjective experience, agency, and accountability remain central challenges, especially as systems become more adaptive and more deeply involved in human decisions.
                </p>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Human–AI co-design and human-in-the-loop governance</li>
                  <li>• Bounded computation versus unbounded expressivity of mind</li>
                  <li>• Explainability, ethics, accountability, and human dignity</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-serif text-xl text-slate-900 mb-3">Neurotechnology</h3>
                <p className="text-slate-600 leading-relaxed text-sm mb-4">
                  Neurotechnology is presented as a practical bridge between consciousness research and intelligent systems. Brain-computer interfaces and related tools may help connect behavioural data, brain signals, and AI learning, opening new possibilities for assistive technologies, human–AI collaboration, and richer models of cognition. At the same time, this convergence raises deeper questions about oversight, consent, agency, and legal responsibility.
                </p>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Brain-computer interfaces for communication, control, and augmentation</li>
                  <li>• Linking brain data, behaviour, and AI learning</li>
                  <li>• New governance questions for mind–machine collaboration</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                  <Atom className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="font-serif text-xl text-slate-900 mb-3">Fundamental Physics</h3>
                <p className="text-slate-600 leading-relaxed text-sm mb-4">
                  Fundamental physics is treated here as a critical testing ground for theories of consciousness. The research explores whether consciousness, causation, and change may need to be included in our most basic models of reality, especially in relation to relativity, quantum mechanics, gravity, and non-local phenomena. These works propose that current physical frameworks may be incomplete if they leave consciousness out.
                </p>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Consciousness, time, and relativity</li>
                  <li>• Consciousness and quantum mechanics as an interdisciplinary problem</li>
                  <li>• Proposed models linking causation, non-locality, and physical law</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-rose-100 flex items-center justify-center mb-4">
                  <Binary className="w-6 h-6 text-rose-600" />
                </div>
                <h3 className="font-serif text-xl text-slate-900 mb-3">Quantum Computing</h3>
                <p className="text-slate-600 leading-relaxed text-sm mb-4">
                  Quantum computing appears in this research agenda as both a technological frontier and a philosophical challenge. It matters not only because of computational power, but because quantum information, entanglement, measurement, and non-locality sit close to the unresolved questions this work raises about observation, cognition, and reality. In this sense, quantum computing is placed within a wider human-centred debate about intelligence, governance, and the limits of formal systems.
                </p>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Quantum information, entanglement, and non-local reality</li>
                  <li>• Quantum-native intelligence and governance questions</li>
                  <li>• Human-centred design in an era of advanced computation</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Emerging Implications */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="implications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="overline text-brand-primary mb-4">Emerging Implications</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Why This Transformation Matters
            </h2>
            <p className="text-slate-600 leading-relaxed">
              The convergence of these four domains is not merely an academic curiosity — it carries profound implications for how we understand ourselves, govern our technologies, and shape our collective future.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-12 h-12 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <Globe className="w-6 h-6 text-brand-primary" />
              </div>
              <h3 className="font-serif text-lg text-slate-900 mb-2">Scientific Understanding</h3>
              <p className="text-sm text-slate-600">
                Deepening our understanding of consciousness and its place in nature.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-12 h-12 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-brand-primary" />
              </div>
              <h3 className="font-serif text-lg text-slate-900 mb-2">Ethical Governance</h3>
              <p className="text-sm text-slate-600">
                Ensuring transformative technologies serve human flourishing.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-12 h-12 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-brand-primary" />
              </div>
              <h3 className="font-serif text-lg text-slate-900 mb-2">Human Agency</h3>
              <p className="text-sm text-slate-600">
                Preserving and enhancing human autonomy and decision-making capacity.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-12 h-12 rounded-full bg-brand-highlight flex items-center justify-center mx-auto mb-4">
                <Scale className="w-6 h-6 text-brand-primary" />
              </div>
              <h3 className="font-serif text-lg text-slate-900 mb-2">Policy Development</h3>
              <p className="text-sm text-slate-600">
                Informing evidence-based policy for emerging technologies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Perspective */}
      <section className="py-16 lg:py-24 bg-white" data-testid="perspective-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="overline text-brand-primary mb-4">Our Perspective</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
                Navigating the Transformation
              </h2>
              <div className="prose-custom space-y-4">
                <p>
                  The Centre for Consciousness Studies Australia positions itself at the heart of this global transformation. We believe that navigating these changes wisely requires:
                </p>
                <ul className="space-y-3">
                  <li><strong>Rigorous interdisciplinary research</strong> that bridges traditional disciplinary boundaries</li>
                  <li><strong>Thoughtful ethical analysis</strong> grounded in both philosophical rigor and practical wisdom</li>
                  <li><strong>Active policy engagement</strong> that translates research insights into governance frameworks</li>
                  <li><strong>Public communication</strong> that makes complex ideas accessible without oversimplification</li>
                  <li><strong>Global collaboration</strong> with researchers and institutions worldwide</li>
                </ul>
                <p>
                  We are committed to contributing research, analysis, and public engagement that helps humanity navigate this transformation with wisdom, foresight, and care for both present and future generations.
                </p>
              </div>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8 text-white">
              <h3 className="font-serif text-2xl mb-6">Key Questions We Address</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">1</span>
                  </div>
                  <p className="text-slate-300">What is the nature of consciousness, and how does it arise from physical processes?</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">2</span>
                  </div>
                  <p className="text-slate-300">Can artificial systems be genuinely conscious, and how would we know?</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">3</span>
                  </div>
                  <p className="text-slate-300">What ethical frameworks should govern neurotechnology and AI development?</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">4</span>
                  </div>
                  <p className="text-slate-300">How do advances in physics inform our understanding of mind and reality?</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">5</span>
                  </div>
                  <p className="text-slate-300">How can we preserve human agency in an age of intelligent machines?</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* Global Position */}
      <section className="py-16 lg:py-24 bg-slate-900 text-white" data-testid="global-position-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="overline text-blue-300 mb-4">Global Positioning</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-white mb-6">
              An Australian Voice in Global Scholarship
            </h2>
            <p className="text-slate-300 leading-relaxed mb-6">
              Consciousness studies and the ethics of emerging technologies are inherently global concerns. The Centre for Consciousness Studies Australia contributes to international scholarship while bringing a distinctive perspective grounded in Australian research traditions and policy contexts.
            </p>
            <p className="text-slate-300 leading-relaxed">
              We collaborate with researchers and institutions worldwide, participating in global conversations while addressing the specific needs of Australian researchers, policymakers, and the public.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-serif text-3xl text-slate-900 mb-4">
                  Connect with the Centre
                </h2>
                <p className="text-slate-600 mb-6">
                  We welcome engagement from researchers, institutions, policymakers, and anyone interested in the future of consciousness studies and responsible technology development.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
                <Button
                  asChild
                  size="lg"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="contact-btn"
                >
                  <Link to="/contact">
                    Contact Us <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="border-slate-300"
                  data-testid="explore-research-btn"
                >
                  <Link to="/research">Explore Research</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
